/**
 * Korean particles change with the word before them: 이/가, 을/를, 은/는, 과/와 after a final
 * consonant or a vowel, and 으로/로 (로 after ㄹ too). Square names end in a digit, read in
 * Sino-Korean (e4 is "e 사"), so digits count by their reading.
 */
const DIGIT_ENDS: Readonly<Record<string, 'vowel' | 'consonant' | 'rieul'>> = {
  '0': 'consonant', // 영
  '1': 'rieul', // 일
  '2': 'vowel', // 이
  '3': 'consonant', // 삼
  '4': 'vowel', // 사
  '5': 'vowel', // 오
  '6': 'consonant', // 육
  '7': 'rieul', // 칠
  '8': 'rieul', // 팔
  '9': 'vowel', // 구
};

function ending(word: string): 'vowel' | 'consonant' | 'rieul' | null {
  const last = word.trim().at(-1);
  if (!last) return null;
  const code = last.charCodeAt(0);
  if (code >= 0xac00 && code <= 0xd7a3) {
    const final = (code - 0xac00) % 28;
    return final === 0 ? 'vowel' : final === 8 ? 'rieul' : 'consonant';
  }
  return DIGIT_ENDS[last] ?? null;
}

export type Particle = '이/가' | '을/를' | '은/는' | '과/와' | '으로/로';

/** The particle to put after `word`, e.g. josa('라임', '이/가') is '이'. */
export function josa(word: string, particle: Particle): string {
  const end = ending(word);
  const [afterConsonant, afterVowel] = particle.split('/') as [string, string];
  if (particle === '으로/로') return end === 'consonant' ? '으로' : end === null ? '(으)로' : '로';
  // A name in Latin letters, say: write both, as Korean does when it can't tell.
  if (end === null) return `${afterConsonant}(${afterVowel})`;
  return end === 'vowel' ? afterVowel : afterConsonant;
}

/** `word` with its particle attached. */
export function withJosa(word: string, particle: Particle): string {
  return `${word}${josa(word, particle)}`;
}
